import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../Context/AuthContext";
import Loader from "../Loader";
import axios from "axios";
import OrderDelivery from "../creator/OrderDelivery";
import sanitizeFilename from "sanitize-filename";
import { toast } from "react-toastify";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { storage, db, auth } from "../../Context/firebaseConfig";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Send } from "lucide-react";
import {
  FileCard,
  GhostAction,
  GlassCard,
  PrimaryAction,
  SuccessCard,
  UploadDropzone,
} from "../order/OrderPrimitives";
import {
  queueTransactionalEmail,
  transactionalEmails,
} from "../../utils/transactionalEmail";
import { openSafeExternalUrl } from "../../utils/security";

const MAX_DELIVERY_FILES = 1;
const DELIVERY_UPLOAD_STATUSES = new Set(["current", "revision"]);

const OrderActions = ({ orderData, orderNumber }) => {
  const { activeUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);
  const role = activeUser?.profileInfo?.role;
  const normalizedOrderStatus = orderData?.status?.toLowerCase() || "";
  const isDeliveryUploadAllowed = DELIVERY_UPLOAD_STATUSES.has(
    normalizedOrderStatus
  );
  const activeUploadedFiles = uploadedFiles.filter((file) => !file?.rejected);
  const hasFilesProcessing = activeUploadedFiles.some(
    (file) => !file?.watermarkedUrl
  );
  const isWaitingForAcceptance =
    normalizedOrderStatus === "pending" && activeUploadedFiles.length > 0;

  const getUploadBlockedMessage = (status = normalizedOrderStatus) => {
    if (status === "pending" && activeUploadedFiles.length > 0) {
      return "This delivery is already waiting for creator acceptance.";
    }

    if (status === "pending") {
      return "The creator needs to begin this order before delivery files can be uploaded.";
    }

    return "Delivery uploads are not available for this order status.";
  };

  useEffect(() => {
    setUploadedFiles(Array.isArray(orderData?.files) ? orderData.files : []);
  }, [orderData?.files]);

  useEffect(() => {
    if (!orderNumber) return;

    const hasProcessingFiles = uploadedFiles.some(
      (file) =>
        !file?.watermarkedUrl &&
        (file?.processingPercentage !== undefined || file?.originalUrl)
    );

    if (!hasProcessingFiles) return;

    const fetchProcessingProgress = async () => {
      try {
        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId) return false;

        const userDocRef = doc(db, "Users", currentUserId);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) return false;

        const userOrders = userDocSnap.data().orders || {};
        const buckets = ["pending", "current", "previous"];
        const orderKey = orderNumber?.toString();
        let freshOrderData = null;

        for (const bucket of buckets) {
          if (userOrders[bucket]?.[orderKey]) {
            freshOrderData = userOrders[bucket][orderKey];
            break;
          }
        }

        if (freshOrderData?.files && freshOrderData.files.length > 0) {
          const stillProcessing = freshOrderData.files.some(
            (file) =>
              !file?.watermarkedUrl &&
              (file?.processingPercentage !== undefined || file?.originalUrl)
          );

          setUploadedFiles(freshOrderData.files);
          return stillProcessing;
        }

        return false;
      } catch (error) {
        console.error("Error fetching processing progress", error);
        return false;
      }
    };

    const intervalId = setInterval(async () => {
      const stillProcessing = await fetchProcessingProgress();
      if (!stillProcessing) {
        clearInterval(intervalId);
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [orderNumber, uploadedFiles]);

  const updateFilesForUserOrders = async (userId, filesPayload) => {
    if (!userId) return;
    try {
      const userRef = doc(db, "Users", userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;

      const userOrders = userSnap.data().orders || {};
      const buckets = ["pending", "current", "previous"];
      const orderKey = orderNumber?.toString();
      let targetBucket = null;

      for (const bucket of buckets) {
        if (userOrders[bucket]?.[orderKey]) {
          targetBucket = bucket;
          break;
        }
      }

      if (!targetBucket) return;

      await updateDoc(userRef, {
        [`orders.${targetBucket}.${orderKey}.files`]: filesPayload,
      });
    } catch (error) {
      console.error(`Error updating files for user ${userId}`, error);
      throw error;
    }
  };

  const syncFilesToOrders = async (filesPayload) => {
    if (!orderData?.creatorId || !orderData?.editorId) return;
    try {
      await Promise.all([
        updateFilesForUserOrders(orderData.creatorId, filesPayload),
        updateFilesForUserOrders(orderData.editorId, filesPayload),
      ]);
    } catch (error) {
      console.error("Failed to sync uploaded files to order data", error);
    }
  };

  const uploadFile = async (file, originalFileName) => {
    if (!isDeliveryUploadAllowed) {
      toast.error(getUploadBlockedMessage());
      return;
    }

    if (activeUploadedFiles.length >= MAX_DELIVERY_FILES) {
      toast.error(
        `You can upload ${MAX_DELIVERY_FILES} delivery video${
          MAX_DELIVERY_FILES === 1 ? "" : "s"
        } for now. Add the rest through a Google Drive link.`
      );
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFilename(file.name);
      const fileExtension = sanitizedFileName.split(".").pop();
      const fileNameWithoutExt = sanitizedFileName.replace(/\.[^/.]+$/, "");
      const uniqueFileName = `${orderNumber}/${timestamp}_${fileNameWithoutExt}.${fileExtension}`;

      const storageRef = ref(storage, `original/${uniqueFileName}`);

      const userId = auth.currentUser?.uid || "anonymous";
      const storageMetadata = {
        contentType: file.type,
        customMetadata: {
          userId,
          fileId: `${orderNumber}_${timestamp}`,
          orderId: orderNumber,
          originalFileName,
          uploadedAt: new Date().toISOString(),
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file, storageMetadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error("Error uploading file to Firebase Storage", error);
          setLoading(false);
          setUploadProgress(0);
          alert("Error uploading file: " + error.message);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const fileMetadata = {
            id: `${orderNumber}_${timestamp}`,
            name: file.name,
            size: file.size,
            type: file.type,
            userId: userId,
            orderId: orderNumber,
            originalUrl: downloadURL,
            publicUrl: downloadURL,
            uploadedAt: new Date().toISOString(),
          };

          const updatedFiles = [...uploadedFiles, fileMetadata];
          console.log("File uploaded successfully to Firebase Storage", fileMetadata);
          setUploadedFiles(updatedFiles);
          await syncFilesToOrders(updatedFiles);
          setLoading(false);
          setUploadProgress(0);
        }
      );
    } catch (error) {
      console.error("Error uploading file to Firebase Storage", error);
      setLoading(false);
      setUploadProgress(0);
      alert("Error uploading file: " + error.message);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      const originalFileName = file.name;
      const sanitizedFileName = sanitizeFilename(file.name);
      const sanitizedFile = new File([file], sanitizedFileName, {
        type: file.type,
      });
      await uploadFile(sanitizedFile, originalFileName);
      event.target.value = "";
    }
  };

  const handlePickedFile = async (file) => {
    if (!file) return;

    const originalFileName = file.name;
    const sanitizedFileName = sanitizeFilename(file.name);
    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type,
    });
    await uploadFile(sanitizedFile, originalFileName);
  };

  async function lookupEmailByUid(uid) {
    const snap = await getDoc(doc(db, "Users", uid));
    if (!snap.exists()) throw new Error("User not found: " + uid);
    return snap.data().profileInfo.email;
  }

  const deliverOrder = async () => {
    try {
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId) {
        toast.error("User not authenticated");
        return;
      }

      const userDocRef = doc(db, "Users", currentUserId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        toast.error("User document not found");
        return;
      }

      const userOrders = userDocSnap.data().orders || {};
      const buckets = ["pending", "current", "previous"];
      const orderKey = orderNumber?.toString();
      let freshOrderData = null;

      for (const bucket of buckets) {
        if (userOrders[bucket]?.[orderKey]) {
          freshOrderData = userOrders[bucket][orderKey];
          break;
        }
      }

      const orderStatus = freshOrderData?.status || orderData?.status;
      const normalizedFreshStatus = orderStatus?.toLowerCase() || "";

      if (
        normalizedFreshStatus === "submitted" ||
        normalizedFreshStatus === "delivered" ||
        normalizedFreshStatus === "approved" ||
        normalizedFreshStatus === "completed"
      ) {
        toast.error("This order has already been submitted.");
        return;
      }

      const canSubmitDelivery =
        DELIVERY_UPLOAD_STATUSES.has(normalizedFreshStatus);

      if (!canSubmitDelivery) {
        toast.error(getUploadBlockedMessage(normalizedFreshStatus));
        return;
      }

      const currentFiles =
        freshOrderData?.files && freshOrderData.files.length > 0
          ? freshOrderData.files
          : uploadedFiles;
      const submittableFiles = currentFiles.filter((file) => !file?.rejected);

      if (submittableFiles.length === 0) {
        toast.error("Upload a delivery video before submitting the order.");
        return;
      }

      if (submittableFiles.length > MAX_DELIVERY_FILES) {
        toast.error(
          `Only ${MAX_DELIVERY_FILES} delivery video${
            MAX_DELIVERY_FILES === 1 ? "" : "s"
          } can be submitted right now. Please use a Google Drive link for the rest.`
        );
        return;
      }

      const filesWithoutWatermark = submittableFiles.filter(
        (file) => !file?.watermarkedUrl
      );

      console.log("Fresh files from DB:", currentFiles);
      console.log("Submittable files:", submittableFiles);
      console.log("Files without watermark:", filesWithoutWatermark);

      if (filesWithoutWatermark.length > 0) {
        toast.error(
          "Please wait for the video processing to complete. Watermarked versions are being generated."
        );
        return;
      }

      setSubmitting(true);

      await axios.post(`${process.env.REACT_APP_ENDPOINT}/submit-order`, {
        creatorId: freshOrderData?.creatorId || orderData.creatorId,
        editorId: freshOrderData?.editorId || orderData.editorId,
        orderNumber: orderNumber,
        files: submittableFiles,
      });

      const creatorEmail = await lookupEmailByUid(orderData.creatorId);
      await queueTransactionalEmail({
        to: creatorEmail,
        email: transactionalEmails.deliveryReady({ orderNumber }),
      });

      setSubmitting(false);
      toast.success("Order submitted successfully!");
    } catch (error) {
      console.error("Error submitting order", error);
      setSubmitting(false);
      toast.error(
        error?.response?.data?.message || "Failed to submit order. Please try again."
      );
    }
  };

  const getOriginalSignedUrl = async (originalUrl) => {
    let path = originalUrl;

    if (originalUrl.includes("storage.googleapis.com/")) {
      const urlParts = originalUrl.split("storage.googleapis.com/");
      if (urlParts.length > 1) {
        path = urlParts[1].split("?")[0];
      }
    }

    if (path.startsWith("orders_files/")) {
      path = path.substring("orders_files/".length);
    }

    path = path.replace(/^original\/original\//, "original/");
    const filename = path.substring(path.lastIndexOf("/") + 1);

    console.log("Extracted filename:", filename);
    const resp = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/files/original/${filename}`
    );
    console.log("Response from backend:", resp);
    return resp.data.url;
  };

  if (!activeUser) return <Loader />;

  const openEditorFile = async (file) => {
    const isProcessing =
      !file?.watermarkedUrl && file?.processingPercentage !== undefined;
    if (isProcessing) return;

    console.log("File clicked:", file);
    if (orderData?.status === "approved" || orderData?.status === "completed") {
      if (file.originalUrl) {
        const signedVideoUrl = await getOriginalSignedUrl(file.originalUrl);
        console.log("Signed Video URL:", signedVideoUrl);
      }
      return openSafeExternalUrl(file.originalUrl || file.publicUrl);
    }

    return openSafeExternalUrl(file?.watermarkedUrl || file?.publicUrl);
  };

  const isEditor = role?.toLowerCase() === "editor";
  const isApproved =
    orderData.status === "approved" || orderData?.status === "completed";

  if (!isEditor) {
    return (
      <div className="text-left">
        <GlassCard glow="purple" className="bg-white/[0.02]">
          <OrderDelivery orderDetails={orderData} orderNumber={orderNumber} />
        </GlassCard>
      </div>
    );
  }

  if (isApproved) {
    return (
      <div className="space-y-4 text-left">
        <SuccessCard message="Order has been approved by the creator. Final files are available below." />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {orderData?.files?.map((file) => (
            <FileCard
              key={file.id}
              file={file}
              onOpen={() => openEditorFile(file)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 text-left">
      {!isDeliveryUploadAllowed ? (
        <GlassCard
          glow={isWaitingForAcceptance ? "mint" : "purple"}
          className="bg-white/[0.02]"
        >
          <p className="text-sm font-semibold text-white">
            {isWaitingForAcceptance
              ? "Waiting for acceptance"
              : "Order has not started yet"}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-white/50">
            {isWaitingForAcceptance
              ? "This delivery has already been submitted. Wait for the creator to approve it or request a revision."
              : "The creator needs to begin checkout before delivery files can be uploaded."}
          </p>
        </GlassCard>
      ) : activeUploadedFiles.length < MAX_DELIVERY_FILES ? (
        <UploadDropzone
          fileRef={fileRef}
          onFileChange={handleFileChange}
          onPick={handlePickedFile}
          uploading={loading}
          progress={uploadProgress}
        />
      ) : (
        <GlassCard glow="purple" className="bg-white/[0.02]">
          <p className="text-sm font-semibold text-white">
            Delivery upload limit reached
          </p>
          <p className="mt-1 text-xs leading-relaxed text-white/50">
            Upload one video here for now. Send additional files with a Google
            Drive link until multi-video delivery is ready.
          </p>
        </GlassCard>
      )}

      {uploadedFiles?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
            Uploaded files
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {uploadedFiles.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                onOpen={() => openEditorFile(file)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {activeUploadedFiles.length === 0 ? (
          <GhostAction disabled>
            {isDeliveryUploadAllowed ? "No files uploaded" : "Order not started"}
          </GhostAction>
        ) : null}
        <PrimaryAction
          disabled={
            !isDeliveryUploadAllowed ||
            loading ||
            submitting ||
            isWaitingForAcceptance ||
            hasFilesProcessing ||
            activeUploadedFiles.length === 0 ||
            activeUploadedFiles.length > MAX_DELIVERY_FILES
          }
          loading={submitting}
          onClick={deliverOrder}
        >
          <Send size={14} />
          {submitting
            ? "Submitting..."
            : isWaitingForAcceptance
            ? "Waiting for Acceptance"
            : hasFilesProcessing
            ? "Processing Watermark"
            : !isDeliveryUploadAllowed
            ? "Order Not Started"
            : "Submit Order"}
        </PrimaryAction>
      </div>
    </div>
  );
};

export default OrderActions;
